<template>
  <div class="p-6">
    <h2 class="mb-4">Travel Buddy Manager</h2>

    <Tabs value="0">
      <TabList>
        <Tab value="0">Users Meetings History</Tab>
        <Tab value="1">Audit Logs</Tab>
        <Tab value="2">Users Status</Tab>
        <Tab value="3">Anomaly Report</Tab>
      </TabList>

      <TabPanels>
        <!-- ============ Tab 0: History ============ -->
        <TabPanel value="0">
          <!-- Trigger Matching & Cleanup Section -->
          <div class="mb-8 mt-4">
            <h3 class="text-lg font-semibold mb-4">Actions</h3>
            <div class="bg-gray-50 p-4 rounded-lg">
              <p class="text-sm text-gray-600 mb-3">
                Manually trigger the travel buddy matching algorithm or
                cleanup stale future buddies data.
              </p>
              <div class="flex flex-wrap items-center gap-4">
                <Button
                  label="Trigger Matching"
                  icon="pi pi-play"
                  @click="confirmTriggerMatching"
                  :loading="triggerMatchingLoading"
                />
                <Button
                  label="Cleanup Future Buddies"
                  icon="pi pi-eraser"
                  severity="warning"
                  @click="confirmTriggerCleanup"
                  :loading="cleanupLoading"
                />
              </div>
              <div v-if="triggerMatchingResult" class="mt-3 text-sm"
                :class="triggerMatchingSuccess ? 'text-green-700' : 'text-red-700'">
                {{ triggerMatchingResult }}
              </div>
              <div v-if="cleanupResult" class="mt-3">
                <div class="text-sm"
                  :class="cleanupSuccess ? 'text-green-700' : 'text-red-700'">
                  {{ cleanupResultMessage }}
                </div>
                <div v-if="cleanupSuccess && cleanupResult.result?.details?.length"
                  class="mt-2 bg-white p-3 rounded border text-sm max-h-48 overflow-auto">
                  <div class="font-medium mb-1">Affected users:</div>
                  <div v-for="d in cleanupResult.result.details" :key="d.userId"
                    class="text-gray-700 mb-1">
                    <span>{{ d.userName }} — moved {{ (d.movedBuddies || []).length }} buddy(ies)</span>
                    <template v-if="(d.skippedEarlierPlacedAtEnd || []).length">
                      <span class="ml-1">; {{ d.skippedEarlierPlacedAtEnd.length }} placed at end</span>
                      <span class="text-amber-700 font-medium"> (skipped earlier)</span>
                      <span v-if="d.skippedEarlierPlacedAtEnd.length" class="text-gray-500 ml-1">— {{ d.skippedEarlierPlacedAtEnd.join(', ') }}</span>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- User History Section -->
          <div class="mb-8">
            <h3 class="text-lg font-semibold mb-4">User Travel Buddy History</h3>
            <div class="bg-gray-50 p-4 rounded-lg">
              <div class="flex flex-wrap gap-4 items-end mb-4">
                <div class="flex flex-col">
                  <label class="text-sm font-medium text-gray-700 mb-1">User Email</label>
                  <InputText
                    v-model="userHistoryEmail"
                    placeholder="Enter user email"
                    class="w-64"
                    @keyup.enter="loadUserHistory"
                  />
                </div>
                <Button
                  label="Load History"
                  icon="pi pi-search"
                  @click="loadUserHistory"
                  :loading="tbLoading"
                />
                <div class="flex flex-col">
                  <label class="text-sm font-medium text-gray-700 mb-1">Export detail</label>
                  <Dropdown
                    v-model="userHistoryExportDetailLevel"
                    :options="userHistoryExportDetailOptions"
                    optionLabel="label"
                    optionValue="value"
                    class="w-40"
                  />
                </div>
                <Button
                  label="Export History CSV"
                  icon="pi pi-download"
                  severity="secondary"
                  @click="exportUserHistoryClick"
                  :loading="tbLoading"
                />
              </div>

              <!-- User summary -->
              <div v-if="tbUserHistory" class="mb-4">
                <h4 class="font-medium text-gray-700 mb-2">User</h4>
                <div class="flex flex-wrap gap-6 text-sm">
                  <span><strong>Name:</strong> {{ tbUserHistory.user?.name }}</span>
                  <span><strong>Email:</strong> {{ tbUserHistory.user?.email }}</span>
                  <span v-if="tbUserHistory.user?.teams?.length">
                    <strong>Teams:</strong> {{ tbUserHistory.user.teams.join(', ') }}
                  </span>
                  <span>
                    <strong>Activated:</strong>
                    <span :class="tbUserHistory.user?.travelBuddyActivated
                      ? 'text-green-600' : 'text-red-600'">
                      {{ tbUserHistory.user?.travelBuddyActivated ? 'Yes' : 'No' }}
                    </span>
                  </span>
                  <span>
                    <strong>On Holiday:</strong>
                    <span :class="tbUserHistory.user?.onHoliday
                      ? 'text-amber-600' : 'text-green-600'">
                      {{ tbUserHistory.user?.onHoliday ? 'Yes' : 'No' }}
                    </span>
                  </span>
                </div>

                <!-- Summary stats -->
                <div class="mt-3 flex flex-wrap gap-6 text-sm">
                  <span><strong>Total Meetings:</strong>
                    {{ tbUserHistory.summary?.totalMeetings }}</span>
                  <span><strong>Completed:</strong>
                    {{ tbUserHistory.summary?.completedMeetings }}</span>
                  <span><strong>Skipped:</strong>
                    {{ tbUserHistory.summary?.skippedMeetings }}</span>
                  <span><strong>Created (pending):</strong>
                    {{ tbUserHistory.summary?.createdMeetings }}</span>
                  <span><strong>Completion Rate:</strong>
                    {{ tbUserHistory.summary?.completionRate != null
                      ? (tbUserHistory.summary.completionRate * 100).toFixed(0) + '%' : '—' }}
                  </span>
                  <span><strong>Avg Helpfulness:</strong>
                    {{ tbUserHistory.summary?.averageHelpfulnessScore ?? '—' }}</span>
                  <span><strong>Unique Buddies:</strong>
                    {{ tbUserHistory.summary?.uniqueBuddies }}</span>
                </div>

                <!-- Duplicate buddies warning -->
                <div v-if="tbUserHistory.summary?.duplicateBuddies?.length"
                  class="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div class="flex items-center gap-2 mb-2">
                    <i class="pi pi-exclamation-triangle text-amber-600"></i>
                    <strong class="text-sm text-amber-800">Duplicate Buddies Detected</strong>
                  </div>
                  <div v-for="dup in tbUserHistory.summary.duplicateBuddies" :key="dup.buddyId"
                    class="text-sm text-amber-700">
                    {{ dup.buddyName }} ({{ dup.buddyEmail }}) — matched
                    {{ dup.matchCount }} times
                    ({{ dup.matchDates.map(d => formatDate(d)).join(', ') }})
                  </div>
                </div>
              </div>

              <!-- Meetings DataTable -->
              <DataTable
                v-if="tbUserHistory && tbUserHistory.meetings && tbUserHistory.meetings.length > 0"
                :value="tbUserHistory.meetings"
                v-model:expandedRows="expandedMeetingRows"
                :loading="tbLoading"
                dataKey="meetingId"
                responsiveLayout="scroll"
                class="p-datatable-sm"
              >
                <Column expander style="width: 3rem" />
                <Column header="Date">
                  <template #body="{ data }">
                    {{ formatDate(data.meetingDate) }}
                  </template>
                </Column>
                <Column header="Calendar Week" field="calendarWeek" />
                <Column header="Buddy">
                  <template #body="{ data }">
                    <div v-if="data.buddy" class="flex items-center gap-2">
                      <img v-if="data.buddy.picture_profile?.small"
                        :src="data.buddy.picture_profile.small"
                        class="w-7 h-7 rounded-full object-cover"
                        alt="" />
                      <div>
                        <div class="flex items-center gap-1">
                          <span class="font-medium text-sm">{{ data.buddy.name }}</span>
                          <button class="pi pi-copy text-gray-400 hover:text-gray-700 text-xs cursor-pointer"
                            title="Copy name"
                            @click.stop="copyToClipboard(data.buddy.name)" />
                        </div>
                        <div class="flex items-center gap-1">
                          <span class="text-xs text-gray-500">{{ data.buddy.email }}</span>
                          <button class="pi pi-copy text-gray-400 hover:text-gray-700 text-xs cursor-pointer"
                            title="Copy email"
                            @click.stop="copyToClipboard(data.buddy.email)" />
                        </div>
                      </div>
                    </div>
                    <span v-else class="text-gray-400 text-sm">—</span>
                  </template>
                </Column>
                <Column header="Status">
                  <template #body="{ data }">
                    <span class="px-2 py-1 text-xs font-medium rounded-full" :class="{
                      'bg-green-100 text-green-800': data.meetingStatus === 'completed',
                      'bg-red-100 text-red-800': data.meetingStatus === 'skipped',
                      'bg-blue-100 text-blue-800': data.meetingStatus === 'created',
                    }">
                      {{ data.meetingStatus }}
                    </span>
                  </template>
                </Column>
                <Column header="Duration (days)">
                  <template #body="{ data }">
                    {{ data.durationDays ?? '—' }}
                  </template>
                </Column>
                <Column header="Helpfulness">
                  <template #body="{ data }">
                    <span v-if="data.ownFeedback?.hasHelpfulnessScore"
                      class="text-green-600 text-sm">
                      <i class="pi pi-check-circle mr-1"></i>Given
                    </span>
                    <span v-else class="text-gray-400 text-sm">—</span>
                  </template>
                </Column>
                <Column header="Feedback">
                  <template #body="{ data }">
                    <span v-if="data.ownFeedback?.hasFeedback"
                      class="text-green-600 text-sm">
                      <i class="pi pi-check-circle mr-1"></i>Given
                    </span>
                    <span v-else class="text-gray-400 text-sm">—</span>
                  </template>
                </Column>

                <template #expansion="{ data }">
                  <div class="p-3 space-y-3">
                    <div class="text-sm">
                      <strong>Meeting ID:</strong> {{ data.meetingId }}
                    </div>
                    <div v-if="data.ownFeedback" class="bg-white p-3 rounded border">
                      <h5 class="font-semibold text-sm mb-2">Own Feedback</h5>
                      <div class="text-sm space-y-1">
                        <div><strong>Feedback Given:</strong>
                          {{ data.ownFeedback.hasFeedback ? 'Yes' : 'No' }}</div>
                        <div><strong>Helpfulness Score Given:</strong>
                          {{ data.ownFeedback.hasHelpfulnessScore ? 'Yes' : 'No' }}</div>
                        <div><strong>Further Thoughts Given:</strong>
                          {{ data.ownFeedback.hasFurtherThoughts ? 'Yes' : 'No' }}</div>
                        <div><strong>Shared with Buddy:</strong>
                          {{ data.ownFeedback.shareWithTravelBuddy ? 'Yes' : 'No' }}</div>
                        <div v-if="data.ownFeedback.date">
                          <strong>Date:</strong> {{ formatDate(data.ownFeedback.date) }}
                        </div>
                      </div>
                    </div>
                    <div v-if="data.buddyFeedback" class="bg-white p-3 rounded border">
                      <h5 class="font-semibold text-sm mb-2">Buddy's Feedback</h5>
                      <div class="text-sm space-y-1">
                        <div><strong>Feedback Given:</strong>
                          {{ data.buddyFeedback.hasFeedback ? 'Yes' : 'No' }}</div>
                        <div><strong>Helpfulness Score Given:</strong>
                          {{ data.buddyFeedback.hasHelpfulnessScore ? 'Yes' : 'No' }}</div>
                        <div><strong>Further Thoughts Given:</strong>
                          {{ data.buddyFeedback.hasFurtherThoughts ? 'Yes' : 'No' }}</div>
                        <div><strong>Shared with User:</strong>
                          {{ data.buddyFeedback.sharedWithUser ? 'Yes' : 'No' }}</div>
                      </div>
                    </div>
                    <div v-if="!data.ownFeedback && !data.buddyFeedback"
                      class="text-gray-500 text-sm">
                      No feedback for this meeting.
                    </div>
                  </div>
                </template>
              </DataTable>
              <div v-else-if="tbUserHistory
                && (!tbUserHistory.meetings || tbUserHistory.meetings.length === 0)"
                class="text-center text-gray-500 py-6">
                No meetings found for this user.
              </div>
            </div>
          </div>
        </TabPanel>

        <!-- ============ Tab 1: Audit Logs ============ -->
        <TabPanel value="1">
          <div class="mb-8 mt-4">
            <h3 class="text-lg font-semibold mb-4">Audit Logs</h3>
            <div class="bg-gray-50 p-4 rounded-lg">
              <div class="flex flex-wrap gap-4 items-end mb-4">
                <div class="flex flex-col">
                  <label class="text-sm font-medium text-gray-700 mb-1">User Email</label>
                  <InputText
                    v-model="auditLogFilters.userEmail"
                    placeholder="Filter by email"
                    class="w-48"
                  />
                </div>
                <div class="flex flex-col">
                  <label class="text-sm font-medium text-gray-700 mb-1">Event Type</label>
                  <Dropdown
                    v-model="auditLogFilters.eventType"
                    :options="auditLogEventTypes"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="All events"
                    class="w-48"
                    showClear
                  />
                </div>
                <div class="flex flex-col">
                  <label class="text-sm font-medium text-gray-700 mb-1">Source</label>
                  <Dropdown
                    v-model="auditLogFilters.source"
                    :options="auditLogSourceOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="All sources"
                    class="w-40"
                    showClear
                  />
                </div>
                <div class="flex flex-col">
                  <label class="text-sm font-medium text-gray-700 mb-1">Date Range</label>
                  <Calendar
                    v-model="auditLogFilters.dateRange"
                    selectionMode="range"
                    dateFormat="yy-mm-dd"
                    placeholder="Select date range"
                    class="w-56"
                    showIcon
                    :manualInput="false"
                  />
                </div>
                <Button
                  label="Apply Filters"
                  icon="pi pi-search"
                  @click="loadAuditLogs"
                  :loading="tbLoading"
                />
                <Button
                  label="Refresh"
                  icon="pi pi-refresh"
                  severity="secondary"
                  @click="refreshAuditLogs"
                  :loading="tbLoading"
                />
                <Button
                  :label="auditLogExportWithUserInfo
                    ? 'Export CSV (with user info)' : 'Export CSV (ID only)'"
                  icon="pi pi-download"
                  severity="secondary"
                  @click="exportAuditLogsClick"
                  :loading="tbLoading"
                />
                <div class="flex items-center gap-2">
                  <ToggleSwitch v-model="auditLogExportWithUserInfo" />
                  <label class="text-sm text-gray-700">Include user details in export</label>
                </div>
              </div>

              <div v-if="auditLogFilters.matchingRunId"
                class="flex items-center gap-2 mb-3 p-2 bg-indigo-50 rounded border border-indigo-200">
                <span class="text-sm text-indigo-800">
                  Filtered by Run ID: <strong class="font-mono">{{ auditLogFilters.matchingRunId }}</strong>
                </span>
                <Button
                  icon="pi pi-times"
                  severity="secondary"
                  text
                  rounded
                  size="small"
                  @click="auditLogFilters.matchingRunId = null; loadAuditLogs(1)"
                />
              </div>

              <DataTable
                :value="displayAuditLogs"
                :paginator="true"
                :rows="tbAuditLogsLimit"
                :first="(tbAuditLogsPage - 1) * tbAuditLogsLimit"
                :rowsPerPageOptions="[10, 20, 50]"
                :totalRecords="tbAuditLogsTotal"
                :lazy="true"
                :loading="tbLoading"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks
                  NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                @page="onAuditLogPage"
                dataKey="_id"
                responsiveLayout="scroll"
                v-model:expandedRows="expandedAuditLogGroups"
                :rowClass="auditLogRowClass"
              >
                <Column :expander="true" headerStyle="width: 3rem" />
                <Column field="timestamp" header="Timestamp" :sortable="false">
                  <template #body="{ data }">
                    <template v-if="data._isGroup">
                      <span class="text-xs text-gray-500">{{ formatTimestamp(data.timestamp) }}</span>
                    </template>
                    <template v-else>
                      {{ formatTimestamp(data.timestamp) }}
                    </template>
                  </template>
                </Column>
                <Column header="User">
                  <template #body="{ data }">
                    <template v-if="data._isGroup">
                      <div class="flex items-center gap-3">
                        <span class="px-2 py-1 text-xs font-bold rounded-full bg-green-200 text-green-900">
                          Matching Run
                        </span>
                        <span class="font-mono text-xs text-indigo-600 cursor-pointer hover:underline"
                          :title="'Click to filter: ' + data.matchingRunId"
                          @click.stop="filterByMatchingRunId(data.matchingRunId)">
                          {{ data.matchingRunId.slice(-8) }}
                        </span>
                        <span class="text-sm font-medium text-gray-700">
                          {{ data._eventCount }} events
                        </span>
                        <span v-if="data.matchingEligible != null" class="text-xs text-gray-500">
                          — {{ data.matchingEligible }} eligible,
                          <span class="text-green-700 font-medium">{{ data.matchingMatched ?? 0 }} matched</span>,
                          <span class="text-amber-700 font-medium">{{ data.matchingUnmatched ?? 0 }} unmatched</span>
                        </span>
                      </div>
                    </template>
                    <template v-else>
                      <div v-if="data.userId && typeof data.userId === 'object'"
                        class="flex items-center gap-2">
                        <img v-if="data.userId.picture_profile?.small"
                          :src="data.userId.picture_profile.small"
                          class="w-7 h-7 rounded-full object-cover"
                          alt="" />
                        <div>
                          <div class="flex items-center gap-1">
                            <span class="font-medium text-sm">{{ data.userId.name }}</span>
                            <button class="pi pi-copy text-gray-400 hover:text-gray-700 text-xs cursor-pointer"
                              title="Copy name"
                              @click.stop="copyToClipboard(data.userId.name)" />
                          </div>
                          <div class="flex items-center gap-1">
                            <span class="text-xs text-gray-500">{{ data.userId.email }}</span>
                            <button class="pi pi-copy text-gray-400 hover:text-gray-700 text-xs cursor-pointer"
                              title="Copy email"
                              @click.stop="copyToClipboard(data.userId.email)" />
                          </div>
                        </div>
                      </div>
                      <span v-else class="text-xs text-gray-400">{{ data.userId }}</span>
                    </template>
                  </template>
                </Column>
                <Column field="eventType" header="Event Type" style="min-width: 180px">
                  <template #body="{ data }">
                    <template v-if="!data._isGroup">
                      <span class="px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap"
                        :class="eventTypeBadge(data.eventType)">
                        {{ eventTypeLabel(data.eventType) }}
                      </span>
                    </template>
                  </template>
                </Column>
                <Column field="meetingId" header="Meeting ID">
                  <template #body="{ data }">
                    <template v-if="!data._isGroup">
                      <span v-if="data.meetingId" class="text-xs font-mono">
                        {{ data.meetingId }}
                      </span>
                      <span v-else class="text-gray-300">—</span>
                    </template>
                  </template>
                </Column>
                <Column header="Buddy">
                  <template #body="{ data }">
                    <template v-if="!data._isGroup">
                      <div v-if="data.buddyId && typeof data.buddyId === 'object'"
                        class="flex items-center gap-2">
                        <img v-if="data.buddyId.picture_profile?.small"
                          :src="data.buddyId.picture_profile.small"
                          class="w-6 h-6 rounded-full object-cover"
                          alt="" />
                        <div>
                          <div class="flex items-center gap-1">
                            <span class="font-medium text-sm">{{ data.buddyId.name }}</span>
                            <button class="pi pi-copy text-gray-400 hover:text-gray-700 text-xs cursor-pointer"
                              title="Copy name"
                              @click.stop="copyToClipboard(data.buddyId.name)" />
                          </div>
                          <div class="flex items-center gap-1">
                            <span class="text-xs text-gray-500">{{ data.buddyId.email }}</span>
                            <button class="pi pi-copy text-gray-400 hover:text-gray-700 text-xs cursor-pointer"
                              title="Copy email"
                              @click.stop="copyToClipboard(data.buddyId.email)" />
                          </div>
                        </div>
                      </div>
                      <span v-else-if="data.buddyId" class="text-xs text-gray-400">
                        {{ data.buddyId }}
                      </span>
                      <span v-else class="text-gray-300">—</span>
                    </template>
                  </template>
                </Column>
                <Column field="meetingStatus" header="Meeting Status">
                  <template #body="{ data }">
                    <template v-if="!data._isGroup">
                      <span v-if="data.meetingStatus">{{ data.meetingStatus }}</span>
                      <span v-else class="text-gray-300">—</span>
                    </template>
                  </template>
                </Column>
                <Column field="source" header="Source">
                  <template #body="{ data }">
                    <template v-if="!data._isGroup">{{ data.source }}</template>
                  </template>
                </Column>
                <Column field="trigger" header="Trigger">
                  <template #body="{ data }">
                    <template v-if="!data._isGroup">
                      <span v-if="data.trigger" class="px-2 py-1 text-xs font-medium rounded-full"
                        :class="data.trigger === 'manual'
                          ? 'bg-cyan-100 text-cyan-800'
                          : 'bg-gray-100 text-gray-700'">
                        {{ data.trigger }}
                      </span>
                      <span v-else class="text-gray-300">—</span>
                    </template>
                  </template>
                </Column>
                <Column field="sourceContext" header="Context">
                  <template #body="{ data }">
                    <template v-if="!data._isGroup">
                      <span v-if="data.sourceContext" class="text-sm text-gray-700">
                        {{ data.sourceContext }}
                      </span>
                      <span v-else class="text-gray-300">—</span>
                    </template>
                  </template>
                </Column>
                <Column field="reason" header="Reason" style="max-width: 400px">
                  <template #body="{ data }">
                    <template v-if="!data._isGroup">
                      <div v-if="data.reason"
                        class="text-sm text-gray-700 whitespace-pre-wrap break-words">
                        {{ data.reason }}
                      </div>
                      <span v-else class="text-gray-300">—</span>
                    </template>
                  </template>
                </Column>

                <template #expansion="{ data }">
                  <div v-if="data._isGroup" class="p-3">
                    <DataTable :value="data._children" dataKey="_id" responsiveLayout="scroll"
                      class="audit-run-child-table">
                      <Column field="timestamp" header="Timestamp">
                        <template #body="{ data: child }">
                          {{ formatTimestamp(child.timestamp) }}
                        </template>
                      </Column>
                      <Column header="User">
                        <template #body="{ data: child }">
                          <div v-if="child.userId && typeof child.userId === 'object'"
                            class="flex items-center gap-2">
                            <img v-if="child.userId.picture_profile?.small"
                              :src="child.userId.picture_profile.small"
                              class="w-6 h-6 rounded-full object-cover" alt="" />
                            <div>
                              <div class="flex items-center gap-1">
                                <span class="font-medium text-sm">{{ child.userId.name }}</span>
                                <button class="pi pi-copy text-gray-400 hover:text-gray-700 text-xs cursor-pointer"
                                  title="Copy name"
                                  @click.stop="copyToClipboard(child.userId.name)" />
                              </div>
                              <div class="flex items-center gap-1">
                                <span class="text-xs text-gray-500">{{ child.userId.email }}</span>
                                <button class="pi pi-copy text-gray-400 hover:text-gray-700 text-xs cursor-pointer"
                                  title="Copy email"
                                  @click.stop="copyToClipboard(child.userId.email)" />
                              </div>
                            </div>
                          </div>
                          <span v-else class="text-xs text-gray-400">{{ child.userId }}</span>
                        </template>
                      </Column>
                      <Column field="eventType" header="Event Type" style="min-width: 180px">
                        <template #body="{ data: child }">
                          <span class="px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap"
                            :class="eventTypeBadge(child.eventType)">
                            {{ eventTypeLabel(child.eventType) }}
                          </span>
                        </template>
                      </Column>
                      <Column header="Buddy">
                        <template #body="{ data: child }">
                          <div v-if="child.buddyId && typeof child.buddyId === 'object'"
                            class="flex items-center gap-2">
                            <img v-if="child.buddyId.picture_profile?.small"
                              :src="child.buddyId.picture_profile.small"
                              class="w-5 h-5 rounded-full object-cover" alt="" />
                            <div>
                              <div class="flex items-center gap-1">
                                <span class="font-medium text-sm">{{ child.buddyId.name }}</span>
                                <button class="pi pi-copy text-gray-400 hover:text-gray-700 text-xs cursor-pointer"
                                  title="Copy name"
                                  @click.stop="copyToClipboard(child.buddyId.name)" />
                              </div>
                              <div class="flex items-center gap-1">
                                <span class="text-xs text-gray-500">{{ child.buddyId.email }}</span>
                                <button class="pi pi-copy text-gray-400 hover:text-gray-700 text-xs cursor-pointer"
                                  title="Copy email"
                                  @click.stop="copyToClipboard(child.buddyId.email)" />
                              </div>
                            </div>
                          </div>
                          <span v-else-if="child.buddyId" class="text-xs text-gray-400">{{ child.buddyId }}</span>
                          <span v-else class="text-gray-300">—</span>
                        </template>
                      </Column>
                      <Column field="meetingStatus" header="Status">
                        <template #body="{ data: child }">
                          <span v-if="child.meetingStatus">{{ child.meetingStatus }}</span>
                          <span v-else class="text-gray-300">—</span>
                        </template>
                      </Column>
                      <Column field="trigger" header="Trigger">
                        <template #body="{ data: child }">
                          <span v-if="child.trigger" class="px-2 py-1 text-xs font-medium rounded-full"
                            :class="child.trigger === 'manual' ? 'bg-cyan-100 text-cyan-800' : 'bg-gray-100 text-gray-700'">
                            {{ child.trigger }}
                          </span>
                          <span v-else class="text-gray-300">—</span>
                        </template>
                      </Column>
                      <Column field="sourceContext" header="Context">
                        <template #body="{ data: child }">
                          <span v-if="child.sourceContext" class="text-sm text-gray-700">{{ child.sourceContext }}</span>
                          <span v-else class="text-gray-300">—</span>
                        </template>
                      </Column>
                      <Column field="reason" header="Reason" style="max-width: 400px">
                        <template #body="{ data: child }">
                          <div v-if="child.reason" class="text-sm text-gray-700 whitespace-pre-wrap break-words">
                            {{ child.reason }}
                          </div>
                          <span v-else class="text-gray-300">—</span>
                        </template>
                      </Column>
                    </DataTable>
                  </div>
                </template>
              </DataTable>
            </div>
          </div>
        </TabPanel>
        <!-- ============ Tab 2: Users Status ============ -->
        <TabPanel value="2">
          <div class="mb-8 mt-4">
            <h3 class="text-lg font-semibold mb-4">Users Travel Buddy Status</h3>
            <div class="bg-gray-50 p-4 rounded-lg">
              <div class="flex flex-wrap gap-4 items-end mb-4">
                <Button
                  label="Load Users"
                  icon="pi pi-users"
                  @click="loadUsers"
                  :loading="usersLoading"
                />
                <Button
                  label="Match Selected"
                  icon="pi pi-play"
                  severity="success"
                  :disabled="!selectedUsers.length"
                  :loading="selectiveMatchLoading"
                  @click="confirmSelectiveMatch"
                />
                <span v-if="selectedUsers.length" class="text-sm text-gray-600 self-center">
                  {{ selectedUsers.length }} user(s) selected
                </span>
                <div class="flex flex-col">
                  <label class="text-sm font-medium text-gray-700 mb-1">Search</label>
                  <InputText
                    v-model="usersSearchFilter"
                    placeholder="Filter by name or email"
                    class="w-64"
                  />
                </div>
              </div>

              <!-- Matching Report -->
              <div v-if="selectiveMatchReport" class="mb-4">
                <div class="bg-white border border-gray-200 rounded-lg p-4">
                  <div class="flex items-center justify-between mb-3">
                    <h4 class="font-semibold text-sm">
                      <i class="pi pi-file-check mr-1"></i>
                      Matching Report
                      <span class="text-gray-500 font-normal ml-2">
                        ({{ selectiveMatchReport.matched }} matched,
                        {{ selectiveMatchReport.unmatched }} unmatched
                        of {{ selectiveMatchReport.eligible }} selected)
                      </span>
                    </h4>
                    <Button
                      icon="pi pi-times"
                      text
                      size="small"
                      @click="selectiveMatchReport = null"
                    />
                  </div>
                  <div class="space-y-2 max-h-64 overflow-auto">
                    <div v-for="(entry, idx) in selectiveMatchReport.report" :key="idx"
                      class="flex items-start gap-3 px-3 py-2 rounded text-sm"
                      :class="{
                        'bg-green-50 border border-green-200': entry.action === 'matched',
                        'bg-red-50 border border-red-200': entry.action === 'unmatched',
                        'bg-gray-50 border border-gray-200': entry.action === 'skipped',
                        'bg-blue-50 border border-blue-200': entry.action === 'info',
                      }">
                      <i class="mt-0.5" :class="{
                        'pi pi-check-circle text-green-600': entry.action === 'matched',
                        'pi pi-times-circle text-red-600': entry.action === 'unmatched',
                        'pi pi-minus-circle text-gray-500': entry.action === 'skipped',
                        'pi pi-info-circle text-blue-600': entry.action === 'info',
                      }"></i>
                      <div>
                        <span class="font-medium">{{ entry.userName }}</span>
                        <span class="text-gray-500 ml-1">({{ entry.userEmail }})</span>
                        <div class="text-gray-700 mt-0.5">{{ entry.detail }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DataTable
                v-if="users.length"
                :value="filteredUsers"
                v-model:expandedRows="expandedUserRows"
                v-model:selection="selectedUsers"
                :paginator="true"
                :rows="20"
                :rowsPerPageOptions="[10, 20, 50, 100]"
                :loading="usersLoading"
                dataKey="_id"
                responsiveLayout="scroll"
                class="p-datatable-sm"
                @rowExpand="onUserRowExpand"
              >
                <Column selectionMode="multiple" style="width: 3rem" />
                <Column expander style="width: 3rem" />
                <Column field="name" header="Name" :sortable="true" />
                <Column field="email" header="Email" :sortable="true" />
                <Column header="TB Active" :sortable="true"
                  :sortField="'travelBuddySystem.activated'">
                  <template #body="{ data }">
                    <span class="px-2 py-1 text-xs font-medium rounded-full"
                      :class="data.travelBuddySystem?.activated
                        ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                      {{ data.travelBuddySystem?.activated ? 'Active' : 'Inactive' }}
                    </span>
                  </template>
                </Column>
                <Column header="On Holiday" :sortable="true" sortField="onHoliday">
                  <template #body="{ data }">
                    <span class="px-2 py-1 text-xs font-medium rounded-full"
                      :class="data.onHoliday
                        ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'">
                      {{ data.onHoliday ? 'Holiday' : 'Available' }}
                    </span>
                  </template>
                </Column>
                <Column header="Current Meeting">
                  <template #body="{ data }">
                    <span v-if="data.travelBuddySystem?.currentTravelBuddyMeeting"
                      class="text-xs font-mono">
                      {{ data.travelBuddySystem.currentTravelBuddyMeeting }}
                    </span>
                    <span v-else class="text-gray-300">—</span>
                  </template>
                </Column>
                <Column header="Future Buddies" :sortable="true">
                  <template #body="{ data }">
                    {{ data.travelBuddySystem?.futureTravelBuddies?.length || 0 }}
                  </template>
                </Column>
                <Column header="Previous Buddies" :sortable="true">
                  <template #body="{ data }">
                    {{ data.travelBuddySystem?.previousTravelBuddies?.length || 0 }}
                  </template>
                </Column>

                <template #expansion="{ data: user }">
                  <div class="p-4 space-y-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                    <div v-if="expandedUserDetails[user._id] === 'loading'"
                      class="text-center text-gray-500 py-4">
                      <i class="pi pi-spin pi-spinner mr-2"></i> Loading details...
                    </div>
                    <div v-else-if="expandedUserDetails[user._id] === 'error'"
                      class="text-center text-red-500 py-4">
                      <i class="pi pi-exclamation-circle mr-2"></i> Failed to load details.
                    </div>
                    <template v-else-if="expandedUserDetails[user._id]">
                      <!-- Future Travel Buddies -->
                      <div>
                        <h5 class="font-semibold text-sm mb-2">
                          <i class="pi pi-forward mr-1"></i>
                          Future Travel Buddies
                          ({{ user.travelBuddySystem?.futureTravelBuddies?.length || 0 }})
                        </h5>
                        <div v-if="user.travelBuddySystem?.futureTravelBuddies?.length"
                          class="space-y-1">
                          <div v-for="fbId in user.travelBuddySystem.futureTravelBuddies"
                            :key="fbId"
                            class="flex items-center gap-2 px-3 py-2 rounded text-sm"
                            :class="isStaleFutureBuddy(user._id, fbId)
                              ? 'bg-red-50 border border-red-200'
                              : isSkippedOnlyFutureBuddy(user._id, fbId)
                                ? 'bg-amber-50 border border-amber-200'
                                : 'bg-white border border-gray-200'">
                            <div class="flex flex-col">
                              <span v-if="resolveBuddy(fbId)" class="font-medium">
                                {{ resolveBuddy(fbId).name }}
                              </span>
                              <span v-if="resolveBuddy(fbId)" class="text-xs text-gray-500">
                                {{ resolveBuddy(fbId).email }}
                              </span>
                              <span v-else class="font-mono text-xs text-gray-400">
                                {{ fbId }}
                              </span>
                            </div>
                            <span v-if="isStaleFutureBuddy(user._id, fbId)"
                              class="ml-auto flex items-center gap-1 text-red-600 text-xs font-medium">
                              <i class="pi pi-exclamation-triangle"></i>
                              Already met (completed meeting; cleanup to move to previous)
                            </span>
                            <span v-else-if="isSkippedOnlyFutureBuddy(user._id, fbId)"
                              class="ml-auto flex items-center gap-1 text-amber-600 text-xs font-medium">
                              <i class="pi pi-minus-circle"></i>
                              Skipped (at end of list; no completed meeting yet)
                            </span>
                          </div>
                        </div>
                        <div v-else class="text-sm text-gray-400">No future buddies.</div>
                        <Button
                          v-if="userHasStaleBuddies(user)"
                          label="Cleanup This User"
                          icon="pi pi-eraser"
                          severity="danger"
                          size="small"
                          class="mt-2"
                          :loading="!!userCleanupLoading[user._id]"
                          @click="confirmUserCleanup(user)"
                        />
                      </div>

                      <!-- Previous Travel Buddies -->
                      <div>
                        <h5 class="font-semibold text-sm mb-2">
                          <i class="pi pi-history mr-1"></i>
                          Previous Travel Buddies
                          ({{ user.travelBuddySystem?.previousTravelBuddies?.length || 0 }})
                        </h5>
                        <div v-if="user.travelBuddySystem?.previousTravelBuddies?.length"
                          class="space-y-1">
                          <div v-for="(pb, idx) in user.travelBuddySystem.previousTravelBuddies"
                            :key="idx"
                            class="flex items-center gap-3 px-3 py-2 bg-white border
                              border-gray-200 rounded text-sm">
                            <div class="flex flex-col">
                              <template v-if="resolveBuddy(
                                typeof pb === 'object' ? (pb.buddyId || pb._id) : pb)">
                                <span class="font-medium">
                                  {{ resolveBuddy(
                                    typeof pb === 'object'
                                      ? (pb.buddyId || pb._id) : pb).name }}
                                </span>
                                <span class="text-xs text-gray-500">
                                  {{ resolveBuddy(
                                    typeof pb === 'object'
                                      ? (pb.buddyId || pb._id) : pb).email }}
                                </span>
                              </template>
                              <span v-else class="font-mono text-xs text-gray-400">
                                {{ typeof pb === 'object' ? (pb.buddyId || pb._id || pb) : pb }}
                              </span>
                            </div>
                            <span v-if="typeof pb === 'object' && pb.dateOfCompletion"
                              class="ml-auto text-xs text-gray-500">
                              {{ formatDate(pb.dateOfCompletion) }}
                            </span>
                          </div>
                        </div>
                        <div v-else class="text-sm text-gray-400">No previous buddies.</div>
                      </div>

                      <!-- Meeting History -->
                      <div>
                        <h5 class="font-semibold text-sm mb-2">
                          <i class="pi pi-calendar mr-1"></i>
                          Meeting History
                          ({{ expandedUserDetails[user._id].meetings?.length || 0 }})
                        </h5>
                        <DataTable
                          v-if="expandedUserDetails[user._id].meetings?.length"
                          :value="expandedUserDetails[user._id].meetings"
                          dataKey="meetingId"
                          responsiveLayout="scroll"
                          class="p-datatable-sm"
                          :paginator="expandedUserDetails[user._id].meetings.length > 10"
                          :rows="10"
                        >
                          <Column header="Date">
                            <template #body="{ data }">
                              {{ formatDate(data.meetingDate) }}
                            </template>
                          </Column>
                          <Column header="Buddy">
                            <template #body="{ data }">
                              <div v-if="data.buddy" class="flex items-center gap-2">
                                <img v-if="data.buddy.picture_profile?.small"
                                  :src="data.buddy.picture_profile.small"
                                  class="w-6 h-6 rounded-full object-cover"
                                  alt="" />
                                <span class="text-sm">{{ data.buddy.name }}</span>
                              </div>
                              <span v-else class="text-gray-400 text-sm">—</span>
                            </template>
                          </Column>
                          <Column header="Status">
                            <template #body="{ data }">
                              <span class="px-2 py-1 text-xs font-medium rounded-full" :class="{
                                'bg-green-100 text-green-800': data.meetingStatus === 'completed',
                                'bg-red-100 text-red-800': data.meetingStatus === 'skipped',
                                'bg-blue-100 text-blue-800': data.meetingStatus === 'created',
                              }">
                                {{ data.meetingStatus }}
                              </span>
                            </template>
                          </Column>
                          <Column header="Calendar Week" field="calendarWeek" />
                        </DataTable>
                        <div v-else class="text-sm text-gray-400">No meetings found.</div>
                      </div>
                    </template>
                  </div>
                </template>
              </DataTable>
              <div v-else-if="!usersLoading" class="text-center text-gray-500 py-6">
                Click "Load Users" to display user travel buddy status.
              </div>
            </div>
          </div>
        </TabPanel>

        <!-- ============ Tab 3: Anomaly Report ============ -->
        <TabPanel value="3">
          <div class="mb-8 mt-4">
            <h3 class="text-lg font-semibold mb-4">Travel Buddy Anomaly Report</h3>
            <div class="bg-gray-50 p-4 rounded-lg">
              <div class="flex flex-wrap gap-4 items-center mb-4">
                <Button
                  label="Run Anomaly Scan"
                  icon="pi pi-search-plus"
                  severity="warning"
                  @click="runAnomalyScan"
                  :loading="anomalyScanLoading"
                />
                <Button
                  v-if="anomalyScan"
                  label="Refresh"
                  icon="pi pi-refresh"
                  severity="secondary"
                  @click="runAnomalyScan"
                  :loading="anomalyScanLoading"
                />
                <Dropdown
                  v-if="anomalyScan && availableAnomalyTypes.length"
                  v-model="anomalyTypeFilter"
                  :options="availableAnomalyTypes"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="All Anomaly Types"
                  :showClear="true"
                  class="w-64"
                />
              </div>

              <!-- Summary Cards -->
              <div v-if="anomalyScan" class="mb-6">
                <div class="flex flex-wrap gap-4 mb-4">
                  <div class="flex-1 min-w-[160px] bg-white border rounded-lg p-4 text-center">
                    <div class="text-2xl font-bold text-gray-700">
                      {{ anomalyScan.totalScanned }}
                    </div>
                    <div class="text-sm text-gray-500">Users Scanned</div>
                  </div>
                  <div class="flex-1 min-w-[160px] bg-green-50 border border-green-200
                    rounded-lg p-4 text-center">
                    <div class="text-2xl font-bold text-green-700">
                      {{ anomalyScan.healthyCount }}
                    </div>
                    <div class="text-sm text-green-600">Healthy</div>
                  </div>
                  <div class="flex-1 min-w-[160px] bg-red-50 border border-red-200
                    rounded-lg p-4 text-center">
                    <div class="text-2xl font-bold text-red-700">
                      {{ anomalyScan.criticalCount }}
                    </div>
                    <div class="text-sm text-red-600">Critical</div>
                  </div>
                  <div class="flex-1 min-w-[160px] bg-orange-50 border border-orange-200
                    rounded-lg p-4 text-center">
                    <div class="text-2xl font-bold text-orange-700">
                      {{ anomalyScan.anomalousCount }}
                    </div>
                    <div class="text-sm text-orange-600">Anomalous</div>
                  </div>
                  <div class="flex-1 min-w-[160px] bg-gray-100 border border-gray-300
                    rounded-lg p-4 text-center">
                    <div class="text-2xl font-bold text-gray-600">
                      {{ anomalyScan.hintCount }}
                    </div>
                    <div class="text-sm text-gray-500">Hints</div>
                  </div>
                </div>

                <!-- Status Banner -->
                <div class="rounded-lg p-4 mb-4"
                  :class="anomalyTotalIssues === 0
                    ? 'bg-green-100 border border-green-300'
                    : 'bg-red-100 border border-red-300'">
                  <div class="flex items-center gap-2">
                    <i class="text-lg"
                      :class="anomalyTotalIssues === 0
                        ? 'pi pi-check-circle text-green-700'
                        : 'pi pi-exclamation-triangle text-red-700'"></i>
                    <span class="font-semibold"
                      :class="anomalyTotalIssues === 0
                        ? 'text-green-800' : 'text-red-800'">
                      {{ anomalyTotalIssues === 0
                        ? 'All systems healthy — no anomalies detected'
                        : `${anomalyTotalIssues} anomal${anomalyTotalIssues === 1
                          ? 'y' : 'ies'} detected — review required` }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Anomalous Users Table -->
              <DataTable
                v-if="anomalyScan && filteredAnomalyUsers.length"
                :value="filteredAnomalyUsers"
                v-model:expandedRows="expandedAnomalyRows"
                :paginator="filteredAnomalyUsers.length > 20"
                :rows="20"
                :rowsPerPageOptions="[10, 20, 50]"
                :loading="anomalyScanLoading"
                dataKey="userId"
                responsiveLayout="scroll"
                class="p-datatable-sm"
                @rowExpand="onAnomalyRowExpand"
              >
                <Column expander style="width: 3rem" />
                <Column field="name" header="Name" :sortable="true" />
                <Column field="email" header="Email" :sortable="true" />
                <Column header="Status" :sortable="true" sortField="status">
                  <template #body="{ data }">
                    <span class="px-2 py-1 text-xs font-medium rounded-full"
                      :class="{
                        'bg-red-100 text-red-800': data.status === 'critical',
                        'bg-orange-100 text-orange-800': data.status === 'anomalous',
                        'bg-gray-100 text-gray-700': data.status === 'hint',
                      }">
                      {{ data.status.toUpperCase() }}
                    </span>
                  </template>
                </Column>
                <Column header="Issues" :sortable="true" sortField="anomalyCount">
                  <template #body="{ data }">
                    <span class="font-semibold"
                      :class="data.criticalCount > 0 ? 'text-red-600'
                        : data.anomalyCount > 0 ? 'text-orange-600' : 'text-gray-600'">
                      {{ data.anomalyCount }}
                    </span>
                    <span v-if="data.hintCount" class="text-gray-400 ml-1">
                      (+{{ data.hintCount }} hint{{ data.hintCount > 1 ? 's' : '' }})
                    </span>
                  </template>
                </Column>
                <Column header="Anomaly Types">
                  <template #body="{ data }">
                    <div class="flex flex-wrap gap-1">
                      <span v-for="t in data.anomalyTypes" :key="t"
                        class="px-2 py-0.5 text-xs rounded-full"
                        :class="anomalyTypeBadge(t)">
                        {{ anomalyTypeLabel(t) }}
                      </span>
                    </div>
                  </template>
                </Column>

                <template #expansion="{ data: row }">
                  <div class="p-4 space-y-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg">
                    <!-- Inline anomalies from scan -->
                    <div v-if="row.anomalies && row.anomalies.length">
                      <div class="flex items-center justify-between mb-2">
                        <h5 class="font-semibold text-sm">
                          <i class="pi pi-exclamation-circle mr-1"></i>
                          Detected Anomalies ({{ row.anomalies.length }})
                        </h5>
                        <Button
                          v-if="getResolvableAnomalyTypes(row.anomalies).length > 0"
                          label="Resolve All"
                          icon="pi pi-wrench"
                          severity="warning"
                          size="small"
                          :loading="!!anomalyRepairLoading[row.userId + '_all']"
                          @click="confirmResolveAll(row)"
                        />
                      </div>
                      <div class="space-y-2">
                        <div v-for="(a, idx) in row.anomalies" :key="idx"
                          class="flex items-start gap-3 px-3 py-2 rounded text-sm"
                          :class="{
                            'bg-red-50 border border-red-200': a.severity === 'critical'
                              || a.severity === 'error',
                            'bg-gray-50 border border-gray-200': a.severity === 'hint',
                          }">
                          <i class="mt-0.5"
                            :class="{
                              'pi pi-times-circle text-red-600':
                                a.severity === 'critical' || a.severity === 'error',
                              'pi pi-info-circle text-gray-500': a.severity === 'hint',
                            }"></i>
                          <div class="flex-1">
                            <div class="flex items-center gap-2 mb-1 flex-wrap">
                              <span class="font-medium px-2 py-0.5 text-xs rounded-full"
                                :class="anomalyTypeBadge(a.type)">
                                {{ anomalyTypeLabel(a.type) }}
                              </span>
                              <span class="text-xs uppercase font-medium"
                                :class="{
                                  'text-red-600': a.severity === 'critical'
                                    || a.severity === 'error',
                                  'text-gray-500': a.severity === 'hint',
                                }">
                                {{ a.severity }}
                              </span>
                            </div>
                            <div class="text-gray-700">{{ a.description }}</div>
                            <div class="mt-1 text-xs text-gray-500">
                              <span v-if="a.stored?.createdAt">
                                <strong>Created:</strong>
                                {{ formatTimestamp(a.stored.createdAt) }}
                              </span>
                              <span v-if="a.stored?.meetingDate" class="ml-3">
                                <strong>Meeting Date:</strong>
                                {{ formatDate(a.stored.meetingDate) }}
                              </span>
                              <span v-if="a.stored?.buddyId" class="ml-3">
                                <strong>Buddy ID:</strong>
                                {{ a.stored.buddyId }}
                              </span>
                              <template v-if="!a.stored?.cause">
                                <span><strong>Stored:</strong>
                                  {{ typeof a.stored === 'object'
                                    ? JSON.stringify(a.stored) : a.stored }}</span>
                              </template>
                              <span class="ml-3"><strong>Expected:</strong>
                                {{ typeof a.expected === 'object'
                                  ? JSON.stringify(a.expected) : a.expected }}</span>
                            </div>
                          </div>
                          <Button
                            v-if="anomalyRepairType(a.type)"
                            :label="a.type === 'ORPHANED_MEETING_POINTER' ? 'Delete' : 'Resolve'"
                            :icon="a.type === 'ORPHANED_MEETING_POINTER' ? 'pi pi-trash' : 'pi pi-check'"
                            :severity="a.type === 'ORPHANED_MEETING_POINTER' ? 'danger' : 'success'"
                            size="small"
                            text
                            :loading="!!anomalyRepairLoading[
                              row.userId + '_' + a.type
                              + (a.stored?.meetingId ? '_' + a.stored.meetingId : '')
                            ]"
                            @click="confirmResolveSingle(row, a)"
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Detailed user info (loaded on expand) -->
                    <div v-if="anomalyUserDetails[row.userId] === 'loading'"
                      class="text-center text-gray-500 py-4">
                      <i class="pi pi-spin pi-spinner mr-2"></i> Loading user details...
                    </div>
                    <div v-else-if="anomalyUserDetails[row.userId] === 'error'"
                      class="text-center text-red-500 py-4">
                      <i class="pi pi-exclamation-circle mr-2"></i> Failed to load details.
                    </div>
                    <template v-else-if="anomalyUserDetails[row.userId]">
                      <div class="bg-white border border-gray-200 rounded-lg p-3">
                        <h5 class="font-semibold text-sm mb-2">
                          <i class="pi pi-user mr-1"></i> User Details
                        </h5>
                        <div class="flex flex-wrap gap-4 text-sm">
                          <span><strong>TB Active:</strong>
                            <span :class="anomalyUserDetails[row.userId]
                              .travelBuddySystem?.activated
                              ? 'text-green-600' : 'text-red-600'">
                              {{ anomalyUserDetails[row.userId]
                                .travelBuddySystem?.activated ? 'Yes' : 'No' }}
                            </span>
                          </span>
                          <span><strong>On Holiday:</strong>
                            <span :class="anomalyUserDetails[row.userId]
                              .travelBuddySystem?.onHoliday
                              ? 'text-amber-600' : 'text-green-600'">
                              {{ anomalyUserDetails[row.userId]
                                .travelBuddySystem?.onHoliday ? 'Yes' : 'No' }}
                            </span>
                          </span>
                          <span><strong>Current Meeting:</strong>
                            <span class="font-mono text-xs">
                              {{ anomalyUserDetails[row.userId]
                                .travelBuddySystem?.currentMeeting || '—' }}
                            </span>
                          </span>
                          <span><strong>Future Buddies:</strong>
                            {{ anomalyUserDetails[row.userId]
                              .travelBuddySystem?.futureBuddiesCount ?? 0 }}
                          </span>
                          <span><strong>Previous Buddies:</strong>
                            {{ anomalyUserDetails[row.userId]
                              .travelBuddySystem?.previousBuddiesCount ?? 0 }}
                          </span>
                        </div>
                      </div>

                      <!-- Recent meetings -->
                      <div v-if="anomalyUserDetails[row.userId].meetings?.length"
                        class="bg-white border border-gray-200 rounded-lg p-3">
                        <h5 class="font-semibold text-sm mb-2">
                          <i class="pi pi-calendar mr-1"></i>
                          Recent Meetings
                          ({{ anomalyUserDetails[row.userId].meetings.length }})
                        </h5>
                        <DataTable
                          :value="anomalyUserDetails[row.userId].meetings"
                          dataKey="meetingId"
                          responsiveLayout="scroll"
                          class="p-datatable-sm"
                          :paginator="anomalyUserDetails[row.userId].meetings.length > 5"
                          :rows="5"
                        >
                          <Column header="Created">
                            <template #body="{ data: m }">
                              {{ formatTimestamp(objectIdToDate(m.meetingId)) }}
                            </template>
                          </Column>
                          <Column header="Meeting Date">
                            <template #body="{ data: m }">
                              {{ formatDate(m.meetingDate) }}
                            </template>
                          </Column>
                          <Column header="Buddy">
                            <template #body="{ data: m }">
                              <div v-if="m.buddy">
                                <span class="font-medium text-sm">{{ m.buddy.name }}</span>
                                <span class="text-xs text-gray-500 ml-1">
                                  ({{ m.buddy.email }})
                                </span>
                              </div>
                              <span v-else class="text-gray-400 text-sm">—</span>
                            </template>
                          </Column>
                          <Column header="Status">
                            <template #body="{ data: m }">
                              <span class="px-2 py-1 text-xs font-medium rounded-full" :class="{
                                'bg-green-100 text-green-800':
                                  m.meetingStatus === 'completed',
                                'bg-red-100 text-red-800': m.meetingStatus === 'skipped',
                                'bg-blue-100 text-blue-800': m.meetingStatus === 'created',
                              }">
                                {{ m.meetingStatus }}
                              </span>
                            </template>
                          </Column>
                          <Column header="Meeting ID">
                            <template #body="{ data: m }">
                              <span class="text-xs font-mono text-gray-400">
                                {{ m.meetingId }}
                              </span>
                            </template>
                          </Column>
                        </DataTable>
                      </div>
                    </template>
                  </div>
                </template>
              </DataTable>

              <div v-else-if="anomalyScan && (!anomalyScan.users || !anomalyScan.users.length)"
                class="text-center py-8">
                <i class="pi pi-check-circle text-green-500 text-4xl mb-3 block"></i>
                <div class="text-green-700 font-semibold text-lg">All Clear</div>
                <div class="text-gray-500 text-sm mt-1">
                  No anomalies detected across {{ anomalyScan.totalScanned }} users.
                </div>
              </div>

              <div v-else-if="!anomalyScanLoading" class="text-center text-gray-500 py-6">
                Click "Run Anomaly Scan" to detect Travel Buddy data inconsistencies.
              </div>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import ToggleSwitch from 'primevue/toggleswitch';
import ConfirmDialog from 'primevue/confirmdialog';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';

const EVENT_TYPES = [
  { label: 'Meeting Created', value: 'meeting_created' },
  { label: 'Meeting Completed', value: 'meeting_completed' },
  { label: 'Meeting Skipped', value: 'meeting_skipped' },
  { label: 'Feedback Submitted', value: 'feedback_submitted' },
  { label: 'User Unmatched', value: 'user_unmatched' },
  { label: 'Matching Run', value: 'matching_run' },
  { label: 'Matching Run Start', value: 'matching_run_start' },
  { label: 'Matching Run End', value: 'matching_run_end' },
  { label: 'Activation Changed', value: 'activation_changed' },
  { label: 'Holiday Changed', value: 'holiday_changed' },
  { label: 'Future Buddies Cleanup', value: 'future_buddies_cleanup' },
  { label: 'Selective Matching Run', value: 'selective_matching_run' },
  { label: 'Anomaly Repair', value: 'anomaly_repair' },
];

const SOURCE_OPTIONS = [
  { label: 'All', value: null },
  { label: 'Frontend', value: 'frontend' },
  { label: 'API', value: 'api' },
];

const EVENT_TYPE_LABELS = Object.fromEntries(
  EVENT_TYPES.map((e) => [e.value, e.label]),
);

const EVENT_TYPE_BADGES = {
  meeting_created: 'bg-blue-100 text-blue-800',
  meeting_completed: 'bg-green-100 text-green-800',
  meeting_skipped: 'bg-red-100 text-red-800',
  feedback_submitted: 'bg-purple-100 text-purple-800',
  user_unmatched: 'bg-amber-100 text-amber-800',
  matching_run: 'bg-indigo-100 text-indigo-800',
  matching_run_start: 'bg-green-200 text-green-900 font-bold',
  matching_run_end: 'bg-green-200 text-green-900 font-bold',
  activation_changed: 'bg-teal-100 text-teal-800',
  holiday_changed: 'bg-orange-100 text-orange-800',
  future_buddies_cleanup: 'bg-pink-100 text-pink-800',
  selective_matching_run: 'bg-violet-100 text-violet-800',
  anomaly_repair: 'bg-emerald-100 text-emerald-800',
};

export default {
  name: 'TravelBuddyManager',
  components: {
    Button,
    DataTable,
    Column,
    Calendar,
    Dropdown,
    InputText,
    ToggleSwitch,
    ConfirmDialog,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
  },
  data() {
    return {
      triggerMatchingLoading: false,
      triggerMatchingResult: null,
      triggerMatchingSuccess: false,

      cleanupLoading: false,
      cleanupResult: null,
      cleanupSuccess: false,

      userHistoryEmail: '',
      userHistoryExportDetailLevel: 'summary',
      userHistoryExportDetailOptions: [
        { label: 'Summary (one row per user)', value: 'summary' },
        { label: 'Per meeting (one row per meeting)', value: 'meetings' },
      ],
      expandedMeetingRows: {},

      expandedAuditLogGroups: {},

      auditLogFilters: {
        userEmail: '',
        eventType: null,
        source: null,
        dateRange: null,
        matchingRunId: null,
      },
      auditLogExportWithUserInfo: false,
      auditLogEventTypes: EVENT_TYPES,
      auditLogSourceOptions: SOURCE_OPTIONS,

      expandedUserRows: {},
      expandedUserDetails: {},
      usersSearchFilter: '',
      userCleanupLoading: {},
      selectedUsers: [],
      selectiveMatchLoading: false,
      selectiveMatchReport: null,

      expandedAnomalyRows: {},
      anomalyUserDetails: {},
      anomalyRepairLoading: {},
      anomalyTypeFilter: null,
    };
  },
  computed: {
    ...mapState('travelBuddy', {
      tbUserHistory: 'userHistory',
      tbAuditLogs: 'auditLogs',
      tbAuditLogsTotal: 'auditLogsTotal',
      tbAuditLogsPage: 'auditLogsPage',
      tbAuditLogsLimit: 'auditLogsLimit',
      tbAuditLogsTotalPages: 'auditLogsTotalPages',
      tbLoading: 'loading',
      users: 'users',
      usersLoading: 'usersLoading',
      anomalyScan: 'anomalyScan',
      anomalyScanLoading: 'anomalyScanLoading',
    }),
    anomalyTotalIssues() {
      if (!this.anomalyScan) return 0;
      return (this.anomalyScan.criticalCount || 0) + (this.anomalyScan.anomalousCount || 0);
    },
    availableAnomalyTypes() {
      if (!this.anomalyScan?.users) return [];
      const types = new Set();
      for (const u of this.anomalyScan.users) {
        for (const t of u.anomalyTypes ?? []) types.add(t);
      }
      return [...types].map((t) => ({ label: this.anomalyTypeLabel(t), value: t }));
    },
    filteredAnomalyUsers() {
      if (!this.anomalyScan?.users) return [];
      if (!this.anomalyTypeFilter) return this.anomalyScan.users;
      return this.anomalyScan.users.filter(
        (u) => (u.anomalyTypes ?? []).includes(this.anomalyTypeFilter),
      );
    },
    usersMap() {
      const map = {};
      this.users.forEach((u) => { map[u._id] = u; });
      return map;
    },
    filteredUsers() {
      const activeUsers = this.users.filter((u) => u.userStatus === 'active');
      if (!this.usersSearchFilter) return activeUsers;
      const q = this.usersSearchFilter.toLowerCase();
      return activeUsers.filter(
        (u) => (u.name && u.name.toLowerCase().includes(q))
          || (u.email && u.email.toLowerCase().includes(q)),
      );
    },
    cleanupResultMessage() {
      if (!this.cleanupResult) return '';
      if (!this.cleanupSuccess) return this.cleanupResult;
      if (this.cleanupResult.message) return this.cleanupResult.message;
      const r = this.cleanupResult.result;
      if (!r) return 'Cleanup completed.';
      const skippedMsg = (r.skippedMovedToEndTotal ?? 0) > 0 ? `, ${r.skippedMovedToEndTotal} user(s) moved to end (skipped earlier)` : '';
      return `Scanned ${r.usersScanned} users, fixed ${r.usersFixed}, moved ${r.entriesMoved} entries${skippedMsg}.`;
    },
    displayAuditLogs() {
      const logs = this.tbAuditLogs || [];
      const groups = {};
      const result = [];
      const seenRunIds = new Set();

      for (const log of logs) {
        if (!log.matchingRunId) {
          result.push(log);
          continue;
        }
        if (!groups[log.matchingRunId]) {
          groups[log.matchingRunId] = [];
        }
        groups[log.matchingRunId].push(log);

        if (!seenRunIds.has(log.matchingRunId)) {
          seenRunIds.add(log.matchingRunId);
          result.push({ _id: 'group_' + log.matchingRunId, _isGroup: true, matchingRunId: log.matchingRunId });
        }
      }

      for (const row of result) {
        if (!row._isGroup) continue;
        const children = groups[row.matchingRunId];
        const summary = children.find((c) => c.eventType === 'matching_run_end' || c.eventType === 'matching_run');
        row._children = children;
        row._eventCount = children.length;
        row.timestamp = children[children.length - 1]?.timestamp;
        row.matchingEligible = summary?.matchingEligible;
        row.matchingMatched = summary?.matchingMatched;
        row.matchingUnmatched = summary?.matchingUnmatched;
      }

      return result;
    },
  },
  async mounted() {
    await this.loadAuditLogs(1);
  },
  methods: {
    ...mapActions('travelBuddy', [
      'fetchUserHistory',
      'fetchAuditLogs',
      'exportUserHistory',
      'exportAuditLogs',
      'triggerMatching',
      'triggerCleanup',
      'fetchUsers',
      'fetchUserTravelBuddyDetail',
      'triggerCleanupForUser',
      'triggerMatchingForSelected',
      'scanAnomalies',
      'fetchAnomalyUserDetails',
      'repairAnomaly',
      'repairAllAnomalies',
    ]),

    formatDate(value) {
      if (!value) return '—';
      const d = value instanceof Date ? value : new Date(value);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    },

    objectIdToDate(id) {
      if (!id || typeof id !== 'string' || id.length < 8) return null;
      return new Date(parseInt(id.substring(0, 8), 16) * 1000);
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '—';
      const d = new Date(timestamp);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      const ms = String(d.getMilliseconds()).padStart(3, '0');
      return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}.${ms}`;
    },

    eventTypeLabel(type) {
      return EVENT_TYPE_LABELS[type] || type;
    },

    eventTypeBadge(type) {
      return EVENT_TYPE_BADGES[type] || 'bg-gray-100 text-gray-800';
    },

    auditLogRowClass(data) {
      if (data._isGroup) return 'audit-run-group-header';
      return '';
    },

    copyToClipboard(text) {
      const onSuccess = () => {
        this.$toast.add({ severity: 'info', summary: 'Copied', detail: text, life: 2000 });
      };
      const fallback = () => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          onSuccess();
        } catch {
          this.$toast.add({ severity: 'error', summary: 'Copy failed', life: 2000 });
        }
        document.body.removeChild(textarea);
      };
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(onSuccess).catch(fallback);
      } else {
        fallback();
      }
    },

    confirmTriggerMatching() {
      this.$confirm.require({
        message: 'Are you sure you want to trigger the travel buddy matching algorithm? This will pair eligible users who are not currently in an active meeting.',
        header: 'Confirm Trigger Matching',
        icon: 'pi pi-play',
        acceptLabel: 'Yes, trigger',
        rejectLabel: 'Cancel',
        accept: () => this.executeTriggerMatching(),
      });
    },

    async executeTriggerMatching() {
      this.triggerMatchingLoading = true;
      this.triggerMatchingResult = null;
      try {
        await this.triggerMatching();
        this.triggerMatchingSuccess = true;
        this.triggerMatchingResult = 'Matching triggered successfully.';
      } catch (err) {
        this.triggerMatchingSuccess = false;
        this.triggerMatchingResult = err?.response?.data?.message
          || err?.message || 'Failed to trigger matching.';
      } finally {
        this.triggerMatchingLoading = false;
      }
    },

    confirmTriggerCleanup() {
      this.$confirm.require({
        message: 'This will scan all users and remove any buddy from futureTravelBuddies who already appeared in a past meeting, moving them to previousTravelBuddies. Continue?',
        header: 'Confirm Cleanup Future Buddies',
        icon: 'pi pi-eraser',
        acceptLabel: 'Yes, cleanup',
        rejectLabel: 'Cancel',
        accept: () => this.executeCleanup(),
      });
    },

    async executeCleanup() {
      this.cleanupLoading = true;
      this.cleanupResult = null;
      try {
        const data = await this.triggerCleanup();
        this.cleanupSuccess = true;
        this.cleanupResult = data;
      } catch (err) {
        this.cleanupSuccess = false;
        this.cleanupResult = err?.response?.data?.message
          || err?.message || 'Cleanup failed.';
      } finally {
        this.cleanupLoading = false;
      }
    },

    async loadUserHistory() {
      const email = this.userHistoryEmail?.trim();
      if (!email) return;
      await this.fetchUserHistory(email);
    },

    async exportUserHistoryClick() {
      const params = { detailLevel: this.userHistoryExportDetailLevel };
      if (this.userHistoryEmail?.trim()) {
        params.userEmail = this.userHistoryEmail.trim();
      }
      await this.exportUserHistory(params);
    },

    buildAuditLogFilterParams() {
      const filters = {};
      if (this.auditLogFilters.userEmail) {
        filters.userEmail = this.auditLogFilters.userEmail;
      }
      if (this.auditLogFilters.eventType) {
        filters.eventType = this.auditLogFilters.eventType;
      }
      if (this.auditLogFilters.source) {
        filters.source = this.auditLogFilters.source;
      }
      if (this.auditLogFilters.matchingRunId) {
        filters.matchingRunId = this.auditLogFilters.matchingRunId;
      }
      if (this.auditLogFilters.dateRange && this.auditLogFilters.dateRange.length === 2) {
        const [start, end] = this.auditLogFilters.dateRange;
        filters.startDate = start;
        filters.endDate = end;
      }
      return filters;
    },

    filterByMatchingRunId(runId) {
      this.auditLogFilters.matchingRunId = runId;
      this.loadAuditLogs(1);
    },

    async loadAuditLogs(page = 1) {
      const filters = this.buildAuditLogFilterParams();
      await this.fetchAuditLogs({
        filters,
        page,
        limit: this.tbAuditLogsLimit,
      });
    },

    async refreshAuditLogs() {
      await this.loadAuditLogs(this.tbAuditLogsPage || 1);
    },

    onAuditLogPage(event) {
      const page = (event.page ?? 0) + 1;
      const limit = event.rows ?? this.tbAuditLogsLimit;
      const filters = this.buildAuditLogFilterParams();
      this.fetchAuditLogs({ filters, page, limit });
    },

    async exportAuditLogsClick() {
      const filters = this.buildAuditLogFilterParams();
      filters.includeUserDetails = this.auditLogExportWithUserInfo;
      await this.exportAuditLogs(filters);
    },

    async loadUsers() {
      this.expandedUserRows = {};
      this.expandedUserDetails = {};
      this.selectedUsers = [];
      this.selectiveMatchReport = null;
      await this.fetchUsers();
    },

    async onUserRowExpand(event) {
      const userId = event.data._id;
      if (this.expandedUserDetails[userId] && this.expandedUserDetails[userId] !== 'error') return;
      this.expandedUserDetails[userId] = 'loading';
      try {
        const detail = await this.fetchUserTravelBuddyDetail(userId);
        this.expandedUserDetails[userId] = detail;
      } catch {
        this.expandedUserDetails[userId] = 'error';
      }
    },

    // Stale = buddy is in future list but already has a *completed* meeting (not just skipped).
    // Skipped pairs are not "already met"; they may be placed at end of list and should not show this label.
    isStaleFutureBuddy(userId, futureBuddyId) {
      const detail = this.expandedUserDetails[userId];
      if (!detail || !detail.meetings) return false;
      return detail.meetings.some(
        (m) =>
          m.meetingStatus === 'completed' &&
          m.buddy &&
          (m.buddy._id === futureBuddyId || m.buddy.odId === futureBuddyId),
      );
    },

    // Buddy appears in meeting history only with status 'skipped' (no completed meeting) — e.g. placed at end of list.
    isSkippedOnlyFutureBuddy(userId, futureBuddyId) {
      const detail = this.expandedUserDetails[userId];
      if (!detail || !detail.meetings) return false;
      const matching = detail.meetings.filter(
        (m) => m.buddy && (m.buddy._id === futureBuddyId || m.buddy.odId === futureBuddyId),
      );
      return matching.length > 0 && matching.every((m) => m.meetingStatus === 'skipped');
    },

    getFutureBuddyName(userId, futureBuddyId) {
      const detail = this.expandedUserDetails[userId];
      if (!detail || !detail.meetings) return null;
      const meeting = detail.meetings.find(
        (m) => m.buddy && (m.buddy._id === futureBuddyId || m.buddy.odId === futureBuddyId),
      );
      return meeting?.buddy?.name || null;
    },

    getPreviousBuddyName(userId, previousBuddyId) {
      const detail = this.expandedUserDetails[userId];
      if (!detail || !detail.meetings) return null;
      const meeting = detail.meetings.find(
        (m) => m.buddy && (m.buddy._id === previousBuddyId || m.buddy.odId === previousBuddyId),
      );
      return meeting?.buddy?.name || null;
    },

    resolveBuddy(buddyId) {
      const fromUsers = this.usersMap[buddyId];
      if (fromUsers) return { name: fromUsers.name, email: fromUsers.email };
      return null;
    },

    userHasStaleBuddies(user) {
      const futureBuddies = user.travelBuddySystem?.futureTravelBuddies;
      if (!futureBuddies?.length) return false;
      return futureBuddies.some((fbId) => this.isStaleFutureBuddy(user._id, fbId));
    },

    confirmUserCleanup(user) {
      const staleCount = (user.travelBuddySystem?.futureTravelBuddies || [])
        .filter((fbId) => this.isStaleFutureBuddy(user._id, fbId)).length;
      this.$confirm.require({
        message: `This will remove ${staleCount} stale future buddy(ies) from "${user.name}" and move them to previous travel buddies. Continue?`,
        header: 'Confirm User Cleanup',
        icon: 'pi pi-eraser',
        acceptLabel: 'Yes, cleanup',
        rejectLabel: 'Cancel',
        accept: () => this.executeUserCleanup(user),
      });
    },

    async executeUserCleanup(user) {
      this.userCleanupLoading[user._id] = true;
      try {
        const result = await this.triggerCleanupForUser(user._id);

        if (result?.result?.movedBuddies?.length) {
          const movedSet = new Set(result.result.movedBuddies);
          user.travelBuddySystem.futureTravelBuddies = user.travelBuddySystem
            .futureTravelBuddies.filter((id) => !movedSet.has(id));
          const existing = new Set(
            (user.travelBuddySystem.previousTravelBuddies || [])
              .map((p) => (typeof p === 'object' ? p.buddyId : p)),
          );
          result.result.movedBuddies.forEach((id) => {
            if (!existing.has(id)) {
              user.travelBuddySystem.previousTravelBuddies.push({
                buddyId: id,
                dateOfCompletion: new Date(),
              });
            }
          });
        }

        delete this.expandedUserDetails[user._id];
        const detail = await this.fetchUserTravelBuddyDetail(user._id);
        this.expandedUserDetails[user._id] = detail;
      } catch (err) {
        this.$toast?.add?.({
          severity: 'error',
          summary: 'Error',
          detail: err?.response?.data?.message || err?.message || 'Cleanup failed',
          life: 5000,
        });
      } finally {
        this.userCleanupLoading[user._id] = false;
      }
    },

    confirmSelectiveMatch() {
      const names = this.selectedUsers.map((u) => u.name).join(', ');
      this.$confirm.require({
        message: `Run matching for ${this.selectedUsers.length} selected user(s): ${names}?`,
        header: 'Confirm Selective Matching',
        icon: 'pi pi-play',
        acceptLabel: 'Yes, match',
        rejectLabel: 'Cancel',
        accept: () => this.executeSelectiveMatch(),
      });
    },

    async executeSelectiveMatch() {
      this.selectiveMatchLoading = true;
      this.selectiveMatchReport = null;
      try {
        const userIds = this.selectedUsers.map((u) => u._id);
        const data = await this.triggerMatchingForSelected(userIds);
        this.selectiveMatchReport = data.result || data;
        this.selectedUsers = [];
        await this.fetchUsers();
      } catch (err) {
        this.$toast?.add?.({
          severity: 'error',
          summary: 'Error',
          detail: err?.response?.data?.message || err?.message || 'Selective matching failed',
          life: 5000,
        });
      } finally {
        this.selectiveMatchLoading = false;
      }
    },

    async runAnomalyScan() {
      this.expandedAnomalyRows = {};
      this.anomalyUserDetails = {};
      this.anomalyTypeFilter = null;
      await this.scanAnomalies();
    },

    async onAnomalyRowExpand(event) {
      const { userId } = event.data;
      if (this.anomalyUserDetails[userId] && this.anomalyUserDetails[userId] !== 'error') return;
      this.anomalyUserDetails[userId] = 'loading';
      try {
        const detail = await this.fetchAnomalyUserDetails(userId);
        this.anomalyUserDetails[userId] = detail;
      } catch {
        this.anomalyUserDetails[userId] = 'error';
      }
    },

    anomalyTypeLabel(type) {
      const labels = {
        STALE_FUTURE_BUDDY: 'Stale Future Buddy',
        MEETING_STATUS_INCONSISTENCY: 'Meeting Status Mismatch',
        ORPHANED_MEETING: 'Orphaned Meeting',
        DUPLICATE_PREVIOUS_BUDDY: 'Duplicate Previous Buddy',
        INACTIVE_USER_WITH_MEETING: 'Inactive User w/ Meeting',
        ORPHANED_MEETING_CONCURRENT: 'Concurrent Cron Duplicate',
        ORPHANED_MEETING_POINTER: 'Pointer Corruption (feedback bug)',
      };
      return labels[type] || type;
    },

    anomalyTypeBadge(type) {
      const badges = {
        STALE_FUTURE_BUDDY: 'bg-red-100 text-red-800',
        MEETING_STATUS_INCONSISTENCY: 'bg-orange-100 text-orange-800',
        ORPHANED_MEETING: 'bg-purple-100 text-purple-800',
        DUPLICATE_PREVIOUS_BUDDY: 'bg-amber-100 text-amber-800',
        INACTIVE_USER_WITH_MEETING: 'bg-pink-100 text-pink-800',
        ORPHANED_MEETING_CONCURRENT: 'bg-orange-100 text-orange-800',
        ORPHANED_MEETING_POINTER: 'bg-rose-100 text-rose-800',
      };
      return badges[type] || 'bg-gray-100 text-gray-800';
    },

    anomalyRepairType(type) {
      const map = {
        MEETING_STATUS_INCONSISTENCY: 'clear-completed-meeting',
        DUPLICATE_PREVIOUS_BUDDY: 'deduplicate-previous-buddies',
        STALE_FUTURE_BUDDY: 'stale-future-buddies',
        INACTIVE_USER_WITH_MEETING: 'inactive-user-meeting',
        ORPHANED_MEETING_CONCURRENT: 'orphaned-concurrent',
        ORPHANED_MEETING_POINTER: 'orphaned-pointer',
      };
      return map[type] || null;
    },

    anomalyRepairDescription(type) {
      const map = {
        MEETING_STATUS_INCONSISTENCY: 'Clear the completed/skipped meeting reference',
        DUPLICATE_PREVIOUS_BUDDY: 'Remove duplicate entries, keeping the latest per buddy',
        STALE_FUTURE_BUDDY: 'Move stale future buddies to previousTravelBuddies',
        INACTIVE_USER_WITH_MEETING: 'Clear meeting from deactivated user',
        ORPHANED_MEETING_CONCURRENT: 'Auto-delete same-time cron duplicates, keep the latest one',
        ORPHANED_MEETING_POINTER: 'Delete this specific orphaned meeting (admin choice)',
      };
      return map[type] || 'Resolve this anomaly';
    },

    getResolvableAnomalyTypes(anomalies) {
      return [...new Set(
        anomalies.map((a) => a.type).filter((t) => this.anomalyRepairType(t)),
      )];
    },

    confirmResolveSingle(row, anomaly) {
      const repairType = this.anomalyRepairType(anomaly.type);
      if (!repairType) return;

      const isPointer = anomaly.type === 'ORPHANED_MEETING_POINTER';
      const meetingId = isPointer ? anomaly.stored?.meetingId : null;
      const buddyInfo = anomaly.stored?.buddyId
        ? ` (meeting ${meetingId} with buddy ${anomaly.stored.buddyId})`
        : '';

      this.$confirm.require({
        message: isPointer
          ? `Delete meeting${buddyInfo} for "${row.name}"? The remaining meeting will become the current one.`
          : `${this.anomalyRepairDescription(anomaly.type)} for "${row.name}"?`,
        header: isPointer ? 'Delete Specific Meeting' : 'Confirm Resolve',
        icon: isPointer ? 'pi pi-trash' : 'pi pi-wrench',
        acceptLabel: isPointer ? 'Yes, delete this meeting' : 'Yes, resolve',
        rejectLabel: 'Cancel',
        accept: () => this.executeResolveSingle(
          row, anomaly.type, repairType,
          isPointer ? { meetingId } : undefined,
        ),
      });
    },

    async executeResolveSingle(row, anomalyType, repairType, extraBody) {
      const loadingKey = `${row.userId}_${anomalyType}` + (extraBody?.meetingId ? `_${extraBody.meetingId}` : '');
      this.anomalyRepairLoading[loadingKey] = true;
      try {
        const result = await this.repairAnomaly({
          repairType,
          userId: row.userId,
          extraBody,
        });
        await this.refreshAnomalyRow(row);

        if (result?.affectedBuddies?.length) {
          const needsResolution = result.affectedBuddies.filter((b) => b.needsResolution);
          const autoAssigned = result.affectedBuddies.filter((b) => b.autoAssignedMeeting);
          const freeForMatching = result.affectedBuddies.filter(
            (b) => !b.needsResolution && !b.autoAssignedMeeting,
          );

          let summaryParts = [];
          if (autoAssigned.length) {
            summaryParts.push(
              `Auto-assigned meeting for: ${autoAssigned.map((b) => b.email).join(', ')}`,
            );
          }
          if (freeForMatching.length) {
            summaryParts.push(
              `Free for next matching: ${freeForMatching.map((b) => b.email).join(', ')}`,
            );
          }
          if (summaryParts.length) {
            this.$toast?.add?.({
              severity: 'info',
              summary: 'Affected Buddies',
              detail: summaryParts.join('\n'),
              life: 8000,
            });
          }

          if (needsResolution.length) {
            for (const buddy of needsResolution) {
              this.$confirm.require({
                message: `Buddy "${buddy.name}" (${buddy.email}) has ${buddy.remainingCreatedMeetings} created meetings after cleanup. Resolve for this buddy too?`,
                header: 'Cascading Resolution Needed',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Yes, resolve',
                rejectLabel: 'Skip',
                accept: async () => {
                  try {
                    await this.repairAnomaly({
                      repairType: 'orphaned-concurrent',
                      userId: buddy.buddyId,
                    });
                    this.$toast?.add?.({
                      severity: 'success',
                      summary: 'Buddy Resolved',
                      detail: `Resolved orphaned meetings for ${buddy.email}`,
                      life: 5000,
                    });
                    await this.scanAnomalies();
                  } catch (err) {
                    this.$toast?.add?.({
                      severity: 'error',
                      summary: 'Buddy Repair Failed',
                      detail:
                        err?.response?.data?.message || err?.message || 'Repair failed',
                      life: 5000,
                    });
                  }
                },
              });
            }
          }
        }
      } catch (err) {
        this.$toast?.add?.({
          severity: 'error',
          summary: 'Repair Failed',
          detail: err?.response?.data?.message || err?.message || 'Repair failed',
          life: 5000,
        });
      } finally {
        this.anomalyRepairLoading[loadingKey] = false;
      }
    },

    confirmResolveAll(row) {
      const resolvableTypes = this.getResolvableAnomalyTypes(row.anomalies);
      this.$confirm.require({
        message: `Resolve all ${resolvableTypes.length} fixable anomaly type(s) for "${row.name}"?\n\nActions: ${resolvableTypes.map((t) => this.anomalyRepairDescription(t)).join(', ')}`,
        header: 'Confirm Resolve All',
        icon: 'pi pi-wrench',
        acceptLabel: 'Yes, resolve all',
        rejectLabel: 'Cancel',
        accept: () => this.executeResolveAll(row),
      });
    },

    async executeResolveAll(row) {
      const loadingKey = `${row.userId}_all`;
      this.anomalyRepairLoading[loadingKey] = true;
      try {
        await this.repairAllAnomalies(row.userId);
        await this.refreshAnomalyRow(row);
      } catch (err) {
        this.$toast?.add?.({
          severity: 'error',
          summary: 'Repair Failed',
          detail: err?.response?.data?.message || err?.message || 'Repair failed',
          life: 5000,
        });
      } finally {
        this.anomalyRepairLoading[loadingKey] = false;
      }
    },

    async refreshAnomalyRow(row) {
      try {
        const detail = await this.fetchAnomalyUserDetails(row.userId);
        this.anomalyUserDetails[row.userId] = detail;
        if (detail.anomalies && detail.anomalies.length > 0) {
          row.anomalies = detail.anomalies;
          row.anomalyCount = detail.anomalies.filter(
            (a) => a.severity === 'critical' || a.severity === 'error',
          ).length;
          row.hintCount = detail.anomalies.filter((a) => a.severity === 'hint').length;
          row.criticalCount = detail.anomalies.filter((a) => a.severity === 'critical').length;
          row.status = detail.status;
          row.anomalyTypes = [...new Set(detail.anomalies.map((a) => a.type))];
        } else {
          row.anomalies = [];
          row.anomalyCount = 0;
          row.hintCount = 0;
          row.criticalCount = 0;
          row.status = 'healthy';
          row.anomalyTypes = [];
        }
      } catch {
        // silently ignore refresh errors
      }
    },
  },
};
</script>

<style>
.audit-run-group-header > td {
  background-color: #ecfdf5 !important;
  border-top: 2px solid #10b981 !important;
  border-bottom: 2px solid #10b981 !important;
  cursor: pointer;
}
.audit-run-group-header > td:first-child {
  border-left: 4px solid #10b981 !important;
}
.audit-run-group-header:hover > td {
  background-color: #d1fae5 !important;
}
.audit-run-child-table {
  border-left: 4px solid #a7f3d0;
  margin-left: 0.5rem;
}
</style>
